import { apiGetAuth, serverMessage } from "../calls/apiCalls";
import {
  CREATE_ASSET,
  GET_ALL_POSITIONS,
  GET_COMPANY_GL_ACCOUNTS,
  GET_GROUPS,
  GET_LOCATIONS,
  GET_USERS,
  GET_VENDORS,
} from "../constants/apiUrls";
import { clearAssets, setAssets, setAssetsStatus } from "./assets";
import { bumpCatalogEpoch } from "./catalogSession";
import { clearGLAccounts, setGLAccounts, setGLAccountsStatus } from "./glAccounts";
import { clearGroups, setGroups, setGroupsStatus } from "./groups";
import { clearLocations, setLocations, setLocationsStatus } from "./locations";
import { clearPositions, setPositions, setPositionsStatus } from "./positions";
import { clearUsers, setUsers, setUsersStatus } from "./users";
import { clearVendors, setVendors, setVendorsStatus } from "./vendors";

const catalogs = {
  locations: {
    url: GET_LOCATIONS,
    listKey: "locations",
    select: (state) => state.locations,
    set: setLocations,
    setStatus: setLocationsStatus,
  },
  users: {
    url: GET_USERS,
    listKey: "users",
    select: (state) => state.users,
    set: setUsers,
    setStatus: setUsersStatus,
  },
  positions: {
    url: GET_ALL_POSITIONS,
    listKey: "positions",
    select: (state) => state.positions,
    set: setPositions,
    setStatus: setPositionsStatus,
  },
  groups: {
    url: GET_GROUPS,
    listKey: "groups",
    select: (state) => state.groups,
    set: setGroups,
    setStatus: setGroupsStatus,
  },
  glAccounts: {
    url: GET_COMPANY_GL_ACCOUNTS,
    listKey: "glAccounts",
    select: (state) => state.glAccounts,
    set: setGLAccounts,
    setStatus: setGLAccountsStatus,
  },
  assets: {
    url: CREATE_ASSET,
    listKey: "assets",
    select: (state) => state.assets,
    set: setAssets,
    setStatus: setAssetsStatus,
  },
  vendors: {
    url: GET_VENDORS,
    listKey: "vendors",
    select: (state) => state.vendors,
    set: setVendors,
    setStatus: setVendorsStatus,
  },
};

let generation = 0;
const inflight = new Map();

export function getCatalog(name) {
  return catalogs[name];
}

export function invalidateCatalogRequests() {
  generation += 1;
  inflight.clear();
}

export function clearCompanyCatalogs({ bump = false } = {}) {
  return (dispatch) => {
    invalidateCatalogRequests();
    dispatch(clearLocations());
    dispatch(clearUsers());
    dispatch(clearPositions());
    dispatch(clearGroups());
    dispatch(clearGLAccounts());
    dispatch(clearAssets());
    dispatch(clearVendors());
    if (bump) dispatch(bumpCatalogEpoch());
  };
}

export function ensureCatalog(name, { force = false } = {}) {
  return (dispatch, getState) => {
    const spec = catalogs[name];
    if (!spec) return Promise.resolve(null);

    const current = inflight.get(name);
    if (current) return current;

    const slice = spec.select(getState()) || {};
    const status = slice.status || "idle";
    if (!force && (status === "ready" || status === "loading")) {
      return Promise.resolve(slice[spec.listKey] || []);
    }

    const gen = generation;
    if (status !== "ready") {
      dispatch(spec.setStatus({ status: "loading", error: null }));
    }

    const promise = (async () => {
      try {
        const results = await apiGetAuth(spec.url);
        if (gen !== generation) return null;
        if (Array.isArray(results)) {
          dispatch(spec.set(results));
          return results;
        }
        const message = serverMessage(results) || "Could not load this list.";
        const latest = spec.select(getState()) || {};
        const hasRows =
          Array.isArray(latest[spec.listKey]) && latest[spec.listKey].length > 0;
        dispatch(
          spec.setStatus({
            status: hasRows ? "ready" : "error",
            error: message,
          }),
        );
        return null;
      } finally {
        if (inflight.get(name) === promise) inflight.delete(name);
      }
    })();

    inflight.set(name, promise);
    return promise;
  };
}
